//client/src/pages/JobsDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { RotateCcw, TriangleAlert, Crown, LayoutDashboard, List } from 'lucide-react';
import DataTable from '../components/DataTable';
import LoadingGameOverlay from '../components/LoadingGameOverlay/LoadingGameOverlay';
import { fetchPortalJobsCached } from '../services/portalData';
import { PortalJob } from '../types/portal';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalize(s: unknown): string {
  return (s || '').toString().toLowerCase().trim();
}

function isPhase4(job: PortalJob): boolean {
  return normalize(job.milestone).includes('phase 4');
}

function isVip(job: PortalJob): boolean {
  return (job.isVIP || '').toString().toUpperCase() === 'TRUE';
}

function missingAlert(job: PortalJob): string | null {
  const msel = (job.MSEL || '').trim().toUpperCase() === 'MISSING';
  const sow  = (job.SOW  || '').trim().toUpperCase() === 'MISSING';
  if (msel && sow) return 'Missing MSEL and SOW';
  if (msel) return 'Missing MSEL';
  if (sow)  return 'Missing SOW';
  return null;
}

function jobHaystack(job: PortalJob): string {
  const bits = [
    job.ClientCode, job.ClientName, job.Job_Name, job.returnId,
    job.milestone, job.ClientPartner, job.ClientManager,
    job.JobPartner, job.JobManager, job.CurrentDueDate,
    job.JobWorkStatus, job.ReturnStatusFed, job.ExtensionStatusFed,
    job.ClientOffice, job.ServIndex,
  ];
  return normalize(bits.join(' | '));
}

type Urgency = 'overdue' | 'today' | 'week' | 'soon' | 'later';

function classifyUrgency(dueDateStr: string | undefined): Urgency {
  if (!dueDateStr) return 'later';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dueDateStr);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff < 0)   return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 7)  return 'week';
  if (diff <= 14) return 'soon';
  return 'later';
}

const LANES: { key: Urgency; label: string; color: string; bg: string }[] = [
  { key: 'overdue', label: 'Overdue',        color: '#ff6b6b', bg: 'rgba(255,107,107,.08)' },
  { key: 'today',   label: 'Due Today',       color: '#ffd166', bg: 'rgba(255,209,102,.08)' },
  { key: 'week',    label: 'Due This Week',   color: '#fbbf24', bg: 'rgba(251,191,36,.08)'  },
  { key: 'soon',    label: 'Due in 2 Weeks',  color: '#7aa2ff', bg: 'rgba(122,162,255,.08)' },
];

// ─── Command Center sub-components ───────────────────────────────────────────

interface CardProps {
  job: PortalJob;
  lane: typeof LANES[number];
  onClick: () => void;
}

function ReturnCard({ job, lane, onClick }: CardProps) {
  const [hovered, setHovered] = useState(false);
  const alert = missingAlert(job);
  const vip   = isVip(job);
  const formatted = job.CurrentDueDate
    ? new Date(job.CurrentDueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : '—';

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '11px 13px',
        background: hovered ? 'rgba(0,0,0,.35)' : 'rgba(0,0,0,.22)',
        border: `1px solid ${hovered ? lane.color + '55' : 'var(--border)'}`,
        borderLeft: `3px solid ${lane.color}`,
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'background .12s, border-color .12s',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        minWidth: 0,
      }}
    >
      {/* Row 1: client name + due date */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden', fontWeight: 700, fontSize: 13 }}>
          {alert && <TriangleAlert size={12} color="#fbbf24" style={{ flexShrink: 0 }} />}
          {vip && (
            <span
              title="VIP client"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 16, height: 16, borderRadius: '50%',
                background: '#0f2444', border: '1px solid #7eb8f7',
                color: '#7eb8f7', flexShrink: 0,
              }}
            >
              <Crown size={9} />
            </span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {job.ClientName || '—'}
          </span>
        </div>
        <span style={{
          flexShrink: 0,
          fontSize: 11, fontWeight: 600, letterSpacing: '.01em',
          padding: '2px 7px', borderRadius: 999,
          color: lane.color,
          background: lane.bg,
          border: `1px solid ${lane.color}44`,
        }}>
          {formatted}
        </span>
      </div>

      {/* Row 2: job name */}
      <div className="tiny" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: .85 }}>
        {job.Job_Name || '—'}
        {job.ClientCode && <span style={{ opacity: .6 }}> · {job.ClientCode}</span>}
      </div>

      {/* Row 3: staff + status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
        <span className="tiny muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {job.CurrentStaff || 'Unassigned'}
        </span>
        <span className="tiny muted" style={{ flexShrink: 0, opacity: .7 }}>
          {[job.EntityType, job.JobWorkStatus].filter(Boolean).join(' · ')}
        </span>
      </div>
    </div>
  );
}

interface CommandCenterProps {
  byLane: Record<Urgency, PortalJob[]>;
  staffCapacity: [string, Record<Urgency | 'total', number>][];
  missingCount: number;
  onRowClick: (job: PortalJob) => void;
  onStaffClick: (name: string) => void;
  activeStaff: string;
}

function CommandCenterView({ byLane, staffCapacity, missingCount, onRowClick, onStaffClick, activeStaff }: CommandCenterProps) {
  const totalDisplayed = LANES.reduce((sum, l) => sum + byLane[l.key].length, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* KPI summary bar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {LANES.map((lane) => {
          const count = byLane[lane.key].length;
          return (
            <div
              key={lane.key}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                background: count > 0 ? lane.bg : 'rgba(0,0,0,.15)',
                border: `1px solid ${count > 0 ? lane.color + '44' : 'var(--border)'}`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80,
              }}
            >
              <span style={{ fontSize: 22, fontWeight: 700, color: count > 0 ? lane.color : 'var(--muted)', lineHeight: 1 }}>
                {count}
              </span>
              <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3, whiteSpace: 'nowrap' }}>
                {lane.label}
              </span>
            </div>
          );
        })}
        {missingCount > 0 && (
          <div style={{
            padding: '8px 14px', borderRadius: 10,
            background: 'rgba(251,191,36,.08)', border: '1px solid #fbbf2444',
            display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80,
          }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: '#fbbf24', lineHeight: 1 }}>{missingCount}</span>
            <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>Missing Docs</span>
          </div>
        )}
        <div style={{
          padding: '8px 14px', borderRadius: 10,
          background: 'rgba(0,0,0,.15)', border: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80,
        }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>{totalDisplayed}</span>
          <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>Total</span>
        </div>
      </div>

      {/* Staff Capacity */}
      {staffCapacity.length > 0 && (
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 12px', marginBottom: 10,
            background: 'rgba(100,210,255,.07)', border: '1px solid rgba(100,210,255,.25)',
            borderLeft: '3px solid var(--accent2)', borderRadius: 8,
          }}>
            <span style={{ fontWeight: 700, color: 'var(--accent2)', fontSize: 13 }}>Staff Capacity</span>
            <span className="tiny muted">click to filter</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {staffCapacity.map(([name, counts]) => {
            const isActive = activeStaff === name;
            return (
              <button
                key={name}
                className="btn"
                type="button"
                onClick={() => onStaffClick(isActive ? 'all' : name)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '6px 12px', borderRadius: 8, fontSize: 12,
                  borderColor: isActive ? 'var(--accent)' : undefined,
                  color: isActive ? 'var(--accent)' : undefined,
                  background: isActive ? 'rgba(122,162,255,.12)' : undefined,
                }}
              >
                <span style={{ fontWeight: 600 }}>{name}</span>
                {counts.overdue > 0 && (
                  <span style={{ color: '#ff6b6b', fontWeight: 700 }}>{counts.overdue}↑</span>
                )}
                {(counts.today + counts.week) > 0 && (
                  <span style={{ color: '#fbbf24', fontWeight: 600 }}>{counts.today + counts.week}→</span>
                )}
                <span className="muted">{counts.total}</span>
              </button>
            );
          })}
          </div>
        </div>
      )}

      {/* Urgency lanes */}
      {LANES.map((lane) => {
        const jobs = byLane[lane.key];
        if (jobs.length === 0) return null;
        return (
          <div key={lane.key}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 12px', marginBottom: 10,
              background: lane.bg,
              border: `1px solid ${lane.color}33`,
              borderLeft: `3px solid ${lane.color}`,
              borderRadius: 8,
            }}>
              <span style={{ fontWeight: 700, color: lane.color, fontSize: 13 }}>{lane.label}</span>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999,
                background: `${lane.color}22`, color: lane.color, border: `1px solid ${lane.color}44`,
              }}>
                {jobs.length}
              </span>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 10,
            }}>
              {jobs.map((job) => (
                <ReturnCard
                  key={job.returnId || job.Job_Idx}
                  job={job}
                  lane={lane}
                  onClick={() => onRowClick(job)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {totalDisplayed === 0 && (
        <div className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>
          No returns match the current filters.
        </div>
      )}
    </div>
  );
}

// ─── Filters ─────────────────────────────────────────────────────────────────

interface ApplyFiltersOpts {
  skip?: 'office' | 'partner' | 'manager' | 'workStatus' | 'currentStaff';
  office: string;
  partner: string;
  manager: string;
  workStatus: string;
  currentStaff: string;
  showMissingOnly: boolean;
  showVipOnly: boolean;
}

function applyFilters(jobs: PortalJob[], opts: ApplyFiltersOpts): PortalJob[] {
  return jobs.filter((j) => {
    if (!isPhase4(j)) return false;
    if (opts.skip !== 'office'       && opts.office !== 'all'       && (j.ClientOffice  || '') !== opts.office)             return false;
    if (opts.skip !== 'partner'      && opts.partner !== 'all'      && (j.ClientPartner || '') !== opts.partner)            return false;
    if (opts.skip !== 'manager'      && opts.manager !== 'all'      && (j.ClientManager || '') !== opts.manager)            return false;
    if (opts.skip !== 'workStatus'   && opts.workStatus !== 'all'   && (j.JobWorkStatus  || '').trim() !== opts.workStatus) return false;
    if (opts.skip !== 'currentStaff' && opts.currentStaff !== 'all' && (j.CurrentStaff  || '').trim() !== opts.currentStaff) return false;
    if (opts.showMissingOnly && missingAlert(j) === null) return false;
    if (opts.showVipOnly     && !isVip(j))                return false;
    return true;
  });
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function JobsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [jobs, setJobs]                               = useState<PortalJob[]>([]);
  const [loading, setLoading]                         = useState(false);
  const [error, setError]                             = useState<string | null>(null);
  const [viewMode, setViewMode]                       = useState<'list' | 'command'>('list');
  const [q, setQ]                                     = useState(searchParams.get('q') || '');
  const [workStatus, setWorkStatus]                   = useState(searchParams.get('workStatus') || 'all');
  const [currentStaff, setCurrentStaff]               = useState(searchParams.get('currentStaff') || 'all');
  const [office, setOffice]                           = useState(searchParams.get('office') || 'all');
  const [partner, setPartner]                         = useState(searchParams.get('partner') || 'all');
  const [manager, setManager]                         = useState(searchParams.get('manager') || 'all');
  const [showMissingOnly, setShowMissingOnly]         = useState(false);
  const [showVipOnly, setShowVipOnly]                 = useState(false);

  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set('q', q);
    if (workStatus && workStatus !== 'all') next.set('workStatus', workStatus);
    if (currentStaff && currentStaff !== 'all') next.set('currentStaff', currentStaff);
    if (office  && office  !== 'all') next.set('office', office);
    if (partner && partner !== 'all') next.set('partner', partner);
    if (manager && manager !== 'all') next.set('manager', manager);
    setSearchParams(next, { replace: true });
  }, [q, workStatus, currentStaff, office, partner, manager, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const { data } = await fetchPortalJobsCached();
        if (cancelled) return;
        setJobs(data);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, []);

  // Faceted options — each skips its own key so its list stays stable while selected
  const officeOptions = useMemo(() => {
    const s = new Set<string>();
    applyFilters(jobs, { skip: 'office', office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly })
      .forEach((j) => { const v = (j.ClientOffice || '').trim(); if (v) s.add(v); });
    return Array.from(s).sort();
  }, [jobs, office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly]);

  const partnerOptions = useMemo(() => {
    const s = new Set<string>();
    applyFilters(jobs, { skip: 'partner', office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly })
      .forEach((j) => { const v = (j.ClientPartner || '').trim(); if (v) s.add(v); });
    return Array.from(s).sort();
  }, [jobs, office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly]);

  const managerOptions = useMemo(() => {
    const s = new Set<string>();
    applyFilters(jobs, { skip: 'manager', office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly })
      .forEach((j) => { const v = (j.ClientManager || '').trim(); if (v) s.add(v); });
    return Array.from(s).sort();
  }, [jobs, office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly]);

  const workStatusOptions = useMemo(() => {
    const s = new Set<string>();
    applyFilters(jobs, { skip: 'workStatus', office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly })
      .forEach((j) => { const v = (j.JobWorkStatus || '').trim(); if (v) s.add(v); });
    return Array.from(s).sort();
  }, [jobs, office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly]);

  const currentStaffOptions = useMemo(() => {
    const s = new Set<string>();
    applyFilters(jobs, { skip: 'currentStaff', office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly })
      .forEach((j) => { const v = (j.CurrentStaff || '').trim(); if (v) s.add(v); });
    return Array.from(s).sort();
  }, [jobs, office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly]);

  const filtered = useMemo(() => {
    const query = normalize(q);
    return applyFilters(jobs, { office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly })
      .filter((j) => !query || jobHaystack(j).includes(query));
  }, [jobs, office, partner, manager, workStatus, currentStaff, showMissingOnly, showVipOnly, q]);

  const displayed = filtered;

  const missingCount = useMemo(() => displayed.filter((j) => missingAlert(j) !== null).length, [displayed]);
  const vipCount     = useMemo(() => displayed.filter((j) => isVip(j)).length, [displayed]);

  const kpis = useMemo(() => {
    const uniqueClients = new Set(displayed.map((j) => j.ClientCode || j.ClientName || '').filter(Boolean));
    const phase4Count = jobs.filter((j) => isPhase4(j)).length;
    const now = new Date();
    const in14 = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    const dueSoon = displayed.filter((j) => {
      if (!j.CurrentDueDate) return false;
      const d = new Date(j.CurrentDueDate);
      return d >= now && d <= in14;
    }).length;
    return {
      totalJobs: displayed.length,
      totalLoaded: jobs.length,
      phase4Loaded: phase4Count,
      uniqueClients: uniqueClients.size,
      dueSoon,
    };
  }, [displayed, jobs]);

  const byLane = useMemo(() => {
    const map: Record<Urgency, PortalJob[]> = { overdue: [], today: [], week: [], soon: [], later: [] };
    for (const j of displayed) map[classifyUrgency(j.CurrentDueDate)].push(j);
    for (const k of Object.keys(map) as Urgency[]) {
      map[k].sort((a, b) => new Date(a.CurrentDueDate || '9999').getTime() - new Date(b.CurrentDueDate || '9999').getTime());
    }
    return map;
  }, [displayed]);

  const staffCapacity = useMemo(() => {
    const map = new Map<string, Record<Urgency | 'total', number>>();
    for (const j of displayed) {
      const s = (j.CurrentStaff || 'Unassigned').trim();
      if (!map.has(s)) map.set(s, { overdue: 0, today: 0, week: 0, soon: 0, later: 0, total: 0 });
      const entry = map.get(s)!;
      entry[classifyUrgency(j.CurrentDueDate)]++;
      entry.total++;
    }
    return Array.from(map.entries()).sort((a, b) => b[1].total - a[1].total);
  }, [displayed]);

  const columns = useMemo(() => [
    {
      name: '',
      width: '40px',
      cell: (row: PortalJob) => {
        const msg = missingAlert(row);
        if (!msg) return null;
        return (
          <span data-tag="allowRowEvents" title={msg} style={{ color: '#fbbf24', display: 'flex', alignItems: 'center' }}>
            <TriangleAlert size={15} />
          </span>
        );
      },
    },
    {
      name: 'Client',
      selector: (row: PortalJob) => row.ClientName || '—',
      cell: (row: PortalJob) => (
        <div data-tag="allowRowEvents">
          <div data-tag="allowRowEvents" style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            {row.ClientName || '—'}
            {isVip(row) && (
              <span
                data-tag="allowRowEvents"
                title="Client >$20k in Past Year"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  backgroundColor: '#0f2444', border: '1px solid #7eb8f7',
                  color: '#7eb8f7', flexShrink: 0, cursor: 'default',
                }}
              >
                <Crown size={11} />
              </span>
            )}
          </div>
          <div data-tag="allowRowEvents" className="tiny">{row.ClientCode || '—'} • Office {row.ClientOffice || '—'}</div>
        </div>
      ),
      grow: 2,
    },
    {
      name: 'Job',
      selector: (row: PortalJob) => row.Job_Name || '—',
      cell: (row: PortalJob) => (
        <div data-tag="allowRowEvents">
          <div data-tag="allowRowEvents">{row.Job_Name || '—'}</div>
          <div data-tag="allowRowEvents" className="tiny">returnId: {row.returnId || '—'}</div>
        </div>
      ),
      grow: 2,
    },
    {
      name: 'Management',
      selector: (row: PortalJob) => row.ClientPartner || '—',
      cell: (row: PortalJob) => (
        <div data-tag="allowRowEvents" style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '4px 0' }}>
          {([
            { label: 'Client Partner', value: row.ClientPartner, color: '#7eb8f7', bg: '#1a3a5c' },
            { label: 'Client Manager', value: row.ClientManager, color: '#a78bfa', bg: '#2d1f5e' },
            { label: 'Job Partner',    value: row.JobPartner,    color: '#67e8f9', bg: '#0f3540' },
            { label: 'Job Manager',    value: row.JobManager,    color: '#6ee7b7', bg: '#0f3328' },
          ] as const).map(({ label, value, color, bg }) => (
            <span
              key={label}
              data-tag="allowRowEvents"
              title={label}
              style={{
                display: 'block',
                width: 160, minWidth: 160, maxWidth: 160,
                padding: '2px 7px', borderRadius: 4,
                fontSize: 11, fontWeight: 500,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                backgroundColor: bg, border: `1px solid ${color}55`,
                color, cursor: 'default', boxSizing: 'border-box',
              }}
            >
              {value || '—'}
            </span>
          ))}
        </div>
      ),
      grow: 2,
    },
    {
      name: 'Due',
      selector: (row: PortalJob) => row.CurrentDueDate || '—',
      cell: (row: PortalJob) => {
        const formatted = row.CurrentDueDate
          ? new Date(row.CurrentDueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
          : '—';
        return <span data-tag="allowRowEvents" className="pill">{formatted}</span>;
      },
      width: '140px',
    },
    {
      name: 'Who Has',
      selector: (row: PortalJob) => row.CurrentStaff || '—',
      cell: (row: PortalJob) => <div data-tag="allowRowEvents">{row.CurrentStaff || '—'}</div>,
      grow: 2,
    },
    {
      name: 'Work Status',
      selector: (row: PortalJob) => row.JobWorkStatus || '—',
      cell: (row: PortalJob) => <div data-tag="allowRowEvents">{row.JobWorkStatus || '—'}</div>,
      grow: 2,
    },
    {
      name: 'Fed Status',
      selector: (row: PortalJob) => row.ReturnStatusFed || '—',
      cell: (row: PortalJob) => (
        <div data-tag="allowRowEvents">
          {row.ReturnStatusFed && (
            <div data-tag="allowRowEvents">
              <span className="tiny" style={{ opacity: 0.6 }}>Return: </span>{row.ReturnStatusFed}
            </div>
          )}
          {row.ExtensionStatusFed && (
            <div data-tag="allowRowEvents" className="tiny">
              <span style={{ opacity: 0.6 }}>Ext: </span>{row.ExtensionStatusFed}
            </div>
          )}
          {!row.ReturnStatusFed && !row.ExtensionStatusFed && '—'}
        </div>
      ),
      grow: 2,
    },
  ], []);

  const handleRowClick = (job: PortalJob) => {
    const rid = (job.returnId || '').trim();
    if (!rid) {
      console.warn('[JobsDashboard] clicked row has no returnId; cannot open detail', job);
      return;
    }
    navigate(`/portal/jobs/${encodeURIComponent(rid)}`, {
      state: { from: `${location.pathname}${location.search}`, job },
    });
  };

  const reset = () => {
    setQ('');
    setWorkStatus('all');
    setCurrentStaff('all');
    setOffice('all');
    setPartner('all');
    setManager('all');
    setShowMissingOnly(false);
    setShowVipOnly(false);
  };

  return (
    <>
      <LoadingGameOverlay active={loading} label="Loading return jobs…" />
      <div className="page-stack">
        <section className="card">
          <div className="hd">
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>

                <select value={office} onChange={(e) => setOffice(e.target.value)} title="Office">
                  <option value="all">All Offices</option>
                  {officeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>

                <select value={partner} onChange={(e) => setPartner(e.target.value)} title="Client Partner">
                  <option value="all">All Partners</option>
                  {partnerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>

                <select value={manager} onChange={(e) => setManager(e.target.value)} title="Client Manager">
                  <option value="all">All Managers</option>
                  {managerOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>

                <select value={workStatus} onChange={(e) => setWorkStatus(e.target.value)} title="Work Status">
                  <option value="all">All Work Statuses</option>
                  {workStatusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                <select value={currentStaff} onChange={(e) => setCurrentStaff(e.target.value)} title="Who Has">
                  <option value="all">All Staff</option>
                  {currentStaffOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                <button
                  className="btn"
                  type="button"
                  disabled={missingCount === 0}
                  title={missingCount === 0 ? 'No missing documents' : `${missingCount} job${missingCount !== 1 ? 's' : ''} with missing MSEL/SOW`}
                  onClick={() => setShowMissingOnly((v) => !v)}
                  style={{
                    color: missingCount === 0 ? undefined : '#fbbf24',
                    borderColor: missingCount === 0 ? undefined : '#fbbf24',
                    backgroundColor: showMissingOnly ? '#fbbf2422' : undefined,
                  }}
                >
                  <TriangleAlert size={16} />
                  {missingCount > 0 && <span style={{ marginLeft: 4 }}>{missingCount}</span>}
                </button>

                <button
                  className="btn"
                  type="button"
                  disabled={vipCount === 0}
                  title={vipCount === 0 ? 'No VIP clients' : `${vipCount} VIP client${vipCount !== 1 ? 's' : ''} (>$20k in past year)`}
                  onClick={() => setShowVipOnly((v) => !v)}
                  style={{
                    color: vipCount === 0 ? undefined : '#bfdbfe',
                    borderColor: vipCount === 0 ? undefined : '#bfdbfe',
                    backgroundColor: showVipOnly ? '#bfdbfe22' : undefined,
                  }}
                >
                  <Crown size={16} />
                  {vipCount > 0 && <span style={{ marginLeft: 4 }}>{vipCount}</span>}
                </button>

                <button className="btn" type="button" onClick={reset} title="Reset">
                  <RotateCcw size={16} /> Reset
                </button>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
                <span className="badge">Total: {kpis.totalJobs}</span>
                <span className="badge">Due in 14d: {kpis.dueSoon}</span>
                {/* View toggle */}
                <div style={{
                  display: 'flex', borderRadius: 8, overflow: 'hidden',
                  border: '1px solid var(--border)',
                }}>
                  <button
                    className="btn"
                    type="button"
                    title="List view"
                    onClick={() => setViewMode('list')}
                    style={{
                      borderRadius: 0, border: 'none', padding: '6px 10px',
                      color: viewMode === 'list' ? 'var(--accent)' : undefined,
                      background: viewMode === 'list' ? 'rgba(122,162,255,.12)' : undefined,
                    }}
                  >
                    <List size={15} />
                  </button>
                  <button
                    className="btn"
                    type="button"
                    title="Command center"
                    onClick={() => setViewMode('command')}
                    style={{
                      borderRadius: 0, border: 'none', borderLeft: '1px solid var(--border)', padding: '6px 10px',
                      color: viewMode === 'command' ? 'var(--accent)' : undefined,
                      background: viewMode === 'command' ? 'rgba(122,162,255,.12)' : undefined,
                    }}
                  >
                    <LayoutDashboard size={15} />
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="text"
                placeholder="Search code, name, roles, notes, or narratives…"
                style={{ flex: 1, minWidth: 360 }}
                aria-label="Search jobs"
              />
            </div>
          </div>

          <div className="bd">
            {error ? <div className="muted">Error: {error}</div> : null}
            {viewMode === 'list' ? (
              <>
                <DataTable
                  columns={columns as any}
                  data={displayed}
                  progressPending={loading}
                  pagination
                  onRowClicked={handleRowClick}
                />
                <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
                  Default filter: milestone contains <b>Phase 4</b>. Click any row to open the Return Summary.
                </div>
              </>
            ) : (
              <CommandCenterView
                byLane={byLane}
                staffCapacity={staffCapacity}
                missingCount={missingCount}
                onRowClick={handleRowClick}
                onStaffClick={(name) => setCurrentStaff(name)}
                activeStaff={currentStaff}
              />
            )}
          </div>
        </section>
      </div>
    </>
  );
}
